"use client";

import React, { useState } from "react";
import { useDetailController, type DetailInvoice } from "./useDetailController";
import { ArrowLeft, Trash2, Clock, CheckCircle, AlertCircle } from "lucide-react";

// Layout & Container Components
import { PageContainer } from "@/src/components/layout/page-container";
import { Section } from "@/src/components/layout/section";

// Information Components
import { Text } from "@/src/components/information/text";
import { Badge } from "@/src/components/information/badge";
import { Alert } from "@/src/components/information/alert";
import { Separator } from "@/src/components/information/separator";

// Input Components
import { Button } from "@/src/components/input/button";
import { Input } from "@/src/components/input/input";
import { Textarea } from "@/src/components/input/textarea";
import { FormField } from "@/src/components/input/form-field";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/src/components/input/select";

// Container Components
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from "@/src/components/container/card";
import { SectionCard } from "@/src/components/container/section-card";

// Navigation Components
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/src/components/navigation/tabs";

export interface DetailPageProps {
  invoiceId: string;
}

// Helper: Status Badge with library Badge component
const StatusBadge: React.FC<{ status: DetailInvoice["status"] }> = ({ status }) => {
  const variantMap: Record<DetailInvoice["status"], "default" | "secondary" | "destructive" | "outline"> = {
    pending: "outline",
    approved: "default",
    rejected: "destructive",
    paid: "secondary",
  };

  const labelMap: Record<DetailInvoice["status"], string> = {
    pending: "Pending",
    approved: "Approved",
    rejected: "Rejected",
    paid: "Paid",
  };

  return <Badge variant={variantMap[status]}>{labelMap[status]}</Badge>;
};

// Helper: Currency formatter
const CurrencyDisplay: React.FC<{ amount: number; className?: string }> = ({
  amount,
  className,
}) => {
  const formatted = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);

  return <Text className={className}>{formatted}</Text>;
};

// Helper: Date formatter
const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const formatDateTime = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const DetailPage: React.FC<DetailPageProps> = ({ invoiceId }) => {
  const ctrl = useDetailController(invoiceId);
  const [noteContent, setNoteContent] = useState("");

  // Loading state
  if (ctrl.loading && !ctrl.invoice) {
    return (
      <PageContainer size="xl" className="!px-0 !py-0 flex items-center justify-center min-h-screen">
        <div className="text-center space-y-theme-4">
          <div className="animate-spin w-10 h-10 border-4 border-primary border-t-transparent rounded-full mx-auto" />
          <Text as="p" className="text-muted-foreground">
            Loading invoice...
          </Text>
        </div>
      </PageContainer>
    );
  }

  // Not found state
  if (!ctrl.invoice) {
    return (
      <PageContainer size="xl" className="!px-0 !py-0 flex items-center justify-center min-h-screen">
        <Alert
          variant="destructive"
          title="Invoice Not Found"
          description={`Unable to load invoice ${invoiceId}. Please check the ID and try again.`}
        />
      </PageContainer>
    );
  }

  const invoice = ctrl.invoice;
  const displayData = ctrl.isEditing ? ctrl.formData : invoice;

  return (
    <PageContainer size="xl" scrollable className="!px-0 !py-0 space-y-theme-5">
      {/* Header Section */}
      <div className="space-y-theme-4 animate-fade-in-up">
        {/* Back Link */}
        <a
          href="/invoices"
          className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:text-primary/80 transition"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Invoices
        </a>

        {/* Title & Status Row */}
        <div className="flex items-center justify-between gap-theme-5">
          <div className="flex items-center gap-theme-4">
            <Text as="h2" className="text-h2 font-bold">
              {displayData.number || invoice.number}
            </Text>
            <StatusBadge status={(displayData.status as DetailInvoice["status"]) || invoice.status} />
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-theme-3">
            {!ctrl.isEditing ? (
              <Button
                onClick={() => ctrl.setIsEditing(true)}
                variant="solid"
                color="primary"
              >
                Edit
              </Button>
            ) : (
              <>
                <Button
                  onClick={() => ctrl.saveChanges()}
                  disabled={!ctrl.isDirty || ctrl.loading}
                  variant="solid"
                  color="primary"
                >
                  {ctrl.loading ? "Saving..." : "Save"}
                </Button>
                <Button
                  onClick={() => ctrl.discardChanges()}
                  variant="outline"
                  color="secondary"
                >
                  Cancel
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Vendor Info */}
        <Text as="p" className="text-muted-foreground">
          {displayData.vendor || invoice.vendor}
        </Text>
      </div>

      <Separator />

      {/* Tabs Section */}
      <Tabs defaultValue="overview" value={ctrl.activeTab} onValueChange={(value) => ctrl.setActiveTab(value as "overview" | "lineItems" | "history" | "notes")} className="animate-fade-in-up delay-2">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="lineItems">Line Items</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
          <TabsTrigger value="notes">Notes</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-theme-5 animate-tab-fade">
          <SectionCard header={<CardTitle>Invoice Details</CardTitle>}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-theme-6">
              {/* Left Column */}
              <div className="space-y-theme-5">
                {/* Vendor Field */}
                <FormField
                  label="Vendor"
                  htmlFor="vendor"
                  orientation="vertical"
                >
                  {ctrl.isEditing ? (
                    <Input
                      id="vendor"
                      value={displayData.vendor || ""}
                      onChange={(e) => ctrl.setFormField("vendor", e.target.value)}
                      placeholder="Enter vendor name"
                    />
                  ) : (
                    <Text as="p" className="font-medium">
                      {displayData.vendor}
                    </Text>
                  )}
                </FormField>

                {/* Amount Field */}
                <FormField
                  label="Amount"
                  htmlFor="amount"
                  orientation="vertical"
                >
                  {ctrl.isEditing ? (
                    <Input
                      id="amount"
                      type="number"
                      value={displayData.amount || ""}
                      onChange={(e) => ctrl.setFormField("amount", parseFloat(e.target.value))}
                      placeholder="0.00"
                      step="0.01"
                    />
                  ) : (
                    <CurrencyDisplay amount={displayData.amount || 0} className="text-h5 font-bold" />
                  )}
                </FormField>

                {/* Status Field */}
                <FormField
                  label="Status"
                  htmlFor="status"
                  orientation="vertical"
                >
                  {ctrl.isEditing ? (
                    <Select
                      value={displayData.status || "pending"}
                      onValueChange={(value) =>
                        ctrl.setFormField("status", value)
                      }
                    >
                      <SelectTrigger id="status">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="approved">Approved</SelectItem>
                        <SelectItem value="rejected">Rejected</SelectItem>
                        <SelectItem value="paid">Paid</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <StatusBadge
                      status={displayData.status as DetailInvoice["status"]}
                    />
                  )}
                </FormField>
              </div>

              {/* Right Column */}
              <div className="space-y-theme-5">
                {/* Category Field */}
                <FormField
                  label="Category"
                  htmlFor="category"
                  orientation="vertical"
                >
                  {ctrl.isEditing ? (
                    <Input
                      id="category"
                      value={displayData.category || ""}
                      onChange={(e) => ctrl.setFormField("category", e.target.value)}
                      placeholder="Enter category"
                    />
                  ) : (
                    <Text as="p" className="font-medium">
                      {displayData.category}
                    </Text>
                  )}
                </FormField>

                {/* Due Date Field */}
                <FormField
                  label="Due Date"
                  htmlFor="dueDate"
                  orientation="vertical"
                >
                  {ctrl.isEditing ? (
                    <Input
                      id="dueDate"
                      type="date"
                      value={displayData.dueDate || ""}
                      onChange={(e) => ctrl.setFormField("dueDate", e.target.value)}
                    />
                  ) : (
                    <Text as="p" className="font-medium">
                      {formatDate(displayData.dueDate || "")}
                    </Text>
                  )}
                </FormField>

                {/* Created Date Field (Read-Only) */}
                <FormField
                  label="Created Date"
                  htmlFor="createdDate"
                  orientation="vertical"
                  showLabel={true}
                >
                  <Text as="p" className="text-muted-foreground text-sm">
                    {formatDate(displayData.createdDate || "")}
                  </Text>
                </FormField>
              </div>
            </div>

            {/* Description Field */}
            <div className="pt-theme-4 border-t">
              <FormField
                label="Description"
                htmlFor="description"
                orientation="vertical"
              >
                {ctrl.isEditing ? (
                  <Textarea
                    id="description"
                    value={displayData.description || ""}
                    onChange={(e) => ctrl.setFormField("description", e.target.value)}
                    placeholder="Enter invoice description"
                    className="min-h-[100px]"
                  />
                ) : (
                  <Text as="p" className="text-foreground whitespace-pre-wrap">
                    {displayData.description}
                  </Text>
                )}
              </FormField>
            </div>
          </SectionCard>
        </TabsContent>

        {/* Line Items Tab */}
        <TabsContent value="lineItems" className="space-y-theme-5 animate-tab-fade">
          <SectionCard
            header={<CardTitle>Line Items</CardTitle>}
            footer={
              <div className="flex justify-end">
                <div className="text-right">
                  <Text as="p" className="text-sm text-muted-foreground mb-theme-1">
                    Total Amount
                  </Text>
                  <CurrencyDisplay
                    amount={displayData.amount || 0}
                    className="text-h4 font-bold"
                  />
                </div>
              </div>
            }
          >
            {(!displayData.lineItems || displayData.lineItems.length === 0) ? (
              <Text as="p" className="text-muted-foreground text-center py-theme-6">
                No line items
              </Text>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-theme-3 px-theme-4 font-semibold">
                        Description
                      </th>
                      <th className="text-right py-theme-3 px-theme-4 font-semibold">
                        Qty
                      </th>
                      <th className="text-right py-theme-3 px-theme-4 font-semibold">
                        Unit Price
                      </th>
                      <th className="text-right py-theme-3 px-theme-4 font-semibold">
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayData.lineItems.map((item) => (
                      <tr key={item.id} className="border-b hover:bg-muted/50">
                        <td className="py-theme-3 px-theme-4">{item.description}</td>
                        <td className="text-right py-theme-3 px-theme-4 font-medium">
                          {item.quantity}
                        </td>
                        <td className="text-right py-theme-3 px-theme-4 font-medium">
                          <CurrencyDisplay amount={item.unitPrice} />
                        </td>
                        <td className="text-right py-theme-3 px-theme-4 font-bold">
                          <CurrencyDisplay amount={item.total} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </SectionCard>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="space-y-theme-5 animate-tab-fade">
          {!displayData.history || displayData.history.length === 0 ? (
            <SectionCard>
              <Text as="p" className="text-muted-foreground text-center py-theme-6">
                No history available
              </Text>
            </SectionCard>
          ) : (
            <div className="space-y-theme-4">
              {displayData.history.map((entry, idx) => (
                <SectionCard
                  key={entry.id}
                  className="relative"
                  contentClassName="flex gap-theme-4"
                >
                  {/* Timeline connector */}
                  {idx < (displayData.history?.length || 0) - 1 && (
                    <div className="absolute left-6 top-full h-4 w-0.5 bg-border" />
                  )}

                  {/* Timeline dot */}
                  <div className="flex flex-col items-center flex-shrink-0">
                    <div className="w-6 h-6 rounded-full bg-primary/20 border-2 border-primary flex items-center justify-center flex-shrink-0">
                      {entry.status === "approved" ? (
                        <CheckCircle className="w-4 h-4 text-primary" />
                      ) : (
                        <Clock className="w-4 h-4 text-primary" />
                      )}
                    </div>
                  </div>

                  {/* Event content */}
                  <div className="flex-1">
                    <div className="flex items-center gap-theme-3 mb-theme-2">
                      <Text as="p" className="font-semibold">
                        {entry.action}
                      </Text>
                      <Badge variant="outline">
                        {entry.status.charAt(0).toUpperCase() + entry.status.slice(1)}
                      </Badge>
                    </div>
                    <Text as="p" className="text-sm text-muted-foreground mb-theme-1">
                      by {entry.user}
                    </Text>
                    <Text as="p" className="text-xs text-muted-foreground">
                      {formatDateTime(entry.timestamp)}
                    </Text>
                  </div>
                </SectionCard>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Notes Tab */}
        <TabsContent value="notes" className="space-y-theme-5 animate-tab-fade">
          {/* Add Note Form */}
          <SectionCard
            header={<CardTitle>Add a Note</CardTitle>}
            contentClassName="space-y-theme-4"
            footerClassName="flex justify-end gap-theme-2"
            footer={
              <Button
                onClick={() => {
                  if (noteContent.trim()) {
                    ctrl.addNote(noteContent);
                    setNoteContent("");
                  }
                }}
                disabled={!noteContent.trim()}
                variant="solid"
                color="primary"
              >
                Add Note
              </Button>
            }
          >
            <FormField
              label="Note Content"
              htmlFor="noteContent"
              orientation="vertical"
              showLabel={false}
            >
              <Textarea
                id="noteContent"
                value={noteContent}
                onChange={(e) => setNoteContent(e.target.value)}
                placeholder="Write your note here..."
                className="min-h-[100px]"
              />
            </FormField>
          </SectionCard>

          {/* Notes List */}
          {!displayData.notes || displayData.notes.length === 0 ? (
            <SectionCard>
              <Text as="p" className="text-muted-foreground text-center py-theme-6">
                No notes yet
              </Text>
            </SectionCard>
          ) : (
            <div className="space-y-theme-4">
              {displayData.notes.map((note) => (
                <SectionCard
                  key={note.id}
                  contentClassName="space-y-theme-3"
                >
                  <div className="flex justify-between items-start gap-theme-4">
                    <div>
                      <Text as="p" className="font-semibold">
                        {note.author}
                      </Text>
                      <Text as="p" className="text-xs text-muted-foreground">
                        {formatDateTime(note.timestamp)}
                      </Text>
                    </div>
                    <Button
                      onClick={() => ctrl.deleteNote(note.id)}
                      variant="ghost"
                      color="destructive"
                      size="sm"
                      leftIcon={<Trash2 className="w-4 h-4" />}
                    >
                      Delete
                    </Button>
                  </div>
                  <Text as="p" className="text-foreground whitespace-pre-wrap">
                    {note.content}
                  </Text>
                </SectionCard>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </PageContainer>
  );
};

export default DetailPage;
